import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminAsesoriasPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

const AsesoriaSkeleton = () => (
  <div className="AAP-skeleton">
    <div className="AAP-skeleton-body">
      <div className="AAP-skeleton-line AAP-skeleton-line--short" />
      <div className="AAP-skeleton-line AAP-skeleton-line--medium" />
      <div className="AAP-skeleton-line AAP-skeleton-line--long" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="AAP-stat-card">
    <div className={classNames('AAP-stat-icon', `AAP-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="AAP-stat-content">
      <div className="AAP-stat-value">{value}</div>
      <div className="AAP-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate }) => (
  <div className="AAP-empty-state">
    <i className="bi-chat-dots AAP-empty-icon" />
    <h5 className="AAP-empty-title">Aún no hay asesorías</h5>
    <p className="AAP-empty-description">
      Crea tu primera asesoría para comenzar a gestionar las consultas de los usuarios.
    </p>
    {onCreate && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear asesoría
      </Button>
    )}
  </div>
);

const AdminAsesoriasPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasViewAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_ASESORIAS), [user]);
  const hasCreateAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_ASESORIAS), [user]);
  const hasEditAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_ASESORIAS), [user]);
  const hasDeleteAsesorias = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_ASESORIAS), [user]);
  const hasViewStats = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_ESTADISTICAS_ASESORIAS), [user]);
  const hasViewProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_PROFESORES), [user]);
  const hasCreateProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_PROFESORES), [user]);
  const hasEditProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.EDITAR_PROFESORES), [user]);
  const hasDeleteProfesores = useMemo(() => hasPermission(user, PERMISSION_IDS.ELIMINAR_PROFESORES), [user]);

  // Si no tiene permiso para ver asesorías, redirigir
  useEffect(() => {
    if (user && !hasViewAsesorias) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de asesorías.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewAsesorias, navigate, notify]);

  const [stats, setStats] = useState({ 
    total: 0, 
    disponibles: 0, 
    noDisponibles: 0 
  });
  const [asesorias, setAsesorias] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [search, setSearch] = useState('');
  const [filterProfesor, setFilterProfesor] = useState('');
  const [filterDisponible, setFilterDisponible] = useState('');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingAsesorias, setLoadingAsesorias] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createAsesoriaData, setCreateAsesoriaData] = useState({
    profesor_id: '',
    Zoom: '',
    disponible: true,
    text: '',
    start_time: '',
    end_time: '',
    Precio: '',
  });
  const [creatingAsesoria, setCreatingAsesoria] = useState(false);
  
  // Estados para profesores
  const [expandedProfesores, setExpandedProfesores] = useState(false);
  const [searchProfesores, setSearchProfesores] = useState('');
  const [filterEspecialidad, setFilterEspecialidad] = useState('');
  const [loadingProfesores, setLoadingProfesores] = useState(false);
  const [showCreateProfesorModal, setShowCreateProfesorModal] = useState(false);
  const [createProfesorData, setCreateProfesorData] = useState({
    Nombre: '',
    apellido: '',
    Especialidad: '',
    Descripcion: '',
    Foto: '',
  });
  const [profesorImageFile, setProfesorImageFile] = useState(null);
  const [profesorImagePreview, setProfesorImagePreview] = useState(null);
  const [uploadingProfesorImage, setUploadingProfesorImage] = useState(false);
  const [creatingProfesor, setCreatingProfesor] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token || !hasViewStats) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/asesorias/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats({
        total: data.stats?.total ?? 0,
        disponibles: data.stats?.disponibles ?? 0,
        noDisponibles: data.stats?.noDisponibles ?? 0,
      });
    } catch (error) {
      logError('[AdminAsesoriasPage] Error fetching stats:', error);
      notify({
        title: 'No pudimos obtener las estadísticas',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchProfesores = async (isRefresh = false) => {
    if (!token || !hasViewProfesores) return;
    if (!isRefresh) setLoadingProfesores(true);
    try {
      const params = new URLSearchParams();
      if (searchProfesores) params.append('search', searchProfesores);
      if (filterEspecialidad) params.append('especialidad', filterEspecialidad);

      const response = await fetch(buildApiUrl(`/api/profesores?${params.toString()}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al cargar profesores');
      }
      const data = await response.json();
      setProfesores(data.profesores || []);
    } catch (error) {
      logError('[AdminAsesoriasPage] Error fetching profesores:', error);
      notify({
        title: 'No pudimos cargar los profesores',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingProfesores(false);
    }
  };

  const fetchAsesorias = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingAsesorias(true);
    try {
      const params = new URLSearchParams();
      if (filterProfesor) params.append('profesor_id', filterProfesor);
      if (filterDisponible !== '') params.append('disponible', filterDisponible);
      if (search) params.append('search', search);

      const response = await fetch(buildApiUrl(`/api/asesorias?${params.toString()}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Error al cargar asesorías (${response.status})`);
      }
      const data = await response.json();
      setAsesorias(data.asesorias || []);
    } catch (error) {
      logError('[AdminAsesoriasPage] Error fetching asesorias:', error);
      notify({
        title: 'No pudimos cargar las asesorías',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setLoadingAsesorias(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchProfesores();
  }, [token, hasViewStats, hasViewProfesores]);

  useEffect(() => {
    fetchAsesorias();
  }, [token, filterProfesor, filterDisponible, search]);

  useEffect(() => {
    if (expandedProfesores) {
      fetchProfesores();
    }
  }, [token, searchProfesores, filterEspecialidad, expandedProfesores, hasViewProfesores]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStats(true), fetchAsesorias(true), fetchProfesores(true)]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'Se actualizaron las asesorías exitosamente.',
      variant: 'success',
    });
  };

  const handleProfesorImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfesorImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfesorImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadProfesorImage = async () => {
    if (!profesorImageFile) {
      notify({ title: 'Error', message: 'Selecciona una imagen', variant: 'error' });
      return;
    }

    setUploadingProfesorImage(true);
    try {
      const formData = new FormData();
      formData.append('profesorImage', profesorImageFile);

      const uploadResponse = await fetch(buildApiUrl('/api/profesores/upload-image'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({ error: 'Error desconocido al subir la imagen' }));
        throw new Error(errorData.error || `Error al subir la imagen (${uploadResponse.status})`);
      }

      const uploadData = await uploadResponse.json();
      const imageUrl = uploadData.imageUrl || uploadData.url;

      if (!imageUrl) {
        throw new Error('No se recibió la URL de la imagen');
      }

      setCreateProfesorData(prev => ({ ...prev, Foto: imageUrl }));
      setProfesorImageFile(null);
      setProfesorImagePreview(null);
      notify({
        title: 'Éxito',
        message: 'Foto subida correctamente',
        variant: 'success',
      });
    } catch (error) {
      notify({ title: 'Error', message: error.message, variant: 'error' });
    } finally {
      setUploadingProfesorImage(false);
    }
  };

  const handleCreateProfesor = async () => {
    if (!createProfesorData.Nombre || !createProfesorData.apellido) {
      notify({
        title: 'Campos requeridos',
        message: 'Nombre y Apellido son obligatorios',
        variant: 'warning',
      });
      return;
    }

    setCreatingProfesor(true);
    try {
      const response = await fetch(buildApiUrl('/api/profesores'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createProfesorData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear profesor');
      }

      notify({
        title: 'Profesor creado',
        message: 'El profesor se creó exitosamente.',
        variant: 'success',
      });

      setShowCreateProfesorModal(false);
      setCreateProfesorData({
        Nombre: '',
        apellido: '',
        Especialidad: '',
        Descripcion: '',
        Foto: '',
      });
      setProfesorImageFile(null);
      setProfesorImagePreview(null);
      await fetchProfesores(true);
    } catch (error) {
      notify({
        title: 'Error al crear profesor',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingProfesor(false);
    }
  };

  const handleDeleteProfesor = async (profesorId, profesorName) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar al profesor "${profesorName}"?`)) {
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/api/profesores/${profesorId}`), {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Error al eliminar profesor');
      }

      notify({
        title: 'Éxito',
        message: 'Profesor eliminado exitosamente.',
        variant: 'success',
      });

      await fetchProfesores(true);
    } catch (error) {
      logError('Error eliminando profesor:', error);
      notify({
        title: 'Error',
        message: 'No se pudo eliminar el profesor.',
        variant: 'error',
      });
    }
  };

  // Obtener especialidades únicas para el filtro
  const especialidades = useMemo(() => {
    const unique = [...new Set(profesores.map(p => p.Especialidad).filter(Boolean))];
    return unique.sort();
  }, [profesores]);

  const handleCreateAsesoria = async () => {
    if (!createAsesoriaData.profesor_id || !createAsesoriaData.Precio) {
      notify({
        title: 'Campos requeridos',
        message: 'Profesor y Precio son obligatorios',
        variant: 'warning',
      });
      return;
    }

    setCreatingAsesoria(true);
    try {
      const response = await fetch(buildApiUrl('/api/asesorias'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(createAsesoriaData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al crear asesoría');
      }

      notify({
        title: 'Asesoría creada',
        message: 'La asesoría se creó exitosamente.',
        variant: 'success',
      });

      setShowCreateModal(false);
      setCreateAsesoriaData({
        profesor_id: '',
        Zoom: '',
        disponible: true,
        text: '',
        start_time: '',
        end_time: '',
        Precio: '',
      });
      await fetchAsesorias(true);
      await fetchStats(true);
    } catch (error) {
      notify({
        title: 'Error al crear asesoría',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingAsesoria(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No definida';
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
    }).format(price || 0);
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar activeKey="asesorias" onNavigate={(item) => navigate(item.path)} />
        <main className="admin-main">
          <div className="AAP-container">
            {/* Header */}
            <div className="AAP-header">
              <div>
                <h1 className="AAP-title">
                  <i className="bi-chat-dots" />
                  Asesorías
                </h1>
                <p className="AAP-subtitle">Gestiona las asesorías disponibles para los usuarios</p>
              </div>
              <div className="AAP-header-actions">
                <Button
                  variant="outline-secondary"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                >
                  <i className={`bi-arrow-repeat ${isRefreshing ? 'spinning' : ''}`} />
                </Button>
                {hasCreateAsesorias && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi-plus-circle me-2" />
                    Crear Asesoría
                  </Button>
                )}
              </div>
            </div>

            {/* Stats */}
            {hasViewStats && (
              <div className="AAP-stats">
                {loadingStats ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <StatsCard
                      icon="bi-chat-dots"
                      label="Total"
                      value={stats.total}
                      variant="primary"
                    />
                    <StatsCard
                      icon="bi-check-circle"
                      label="Disponibles"
                      value={stats.disponibles}
                      variant="success"
                    />
                    <StatsCard
                      icon="bi-x-circle"
                      label="No Disponibles"
                      value={stats.noDisponibles}
                      variant="secondary"
                    />
                  </>
                )}
              </div>
            )}

            {/* Filters */}
            <div className="AAP-filters">
              <Form.Control
                type="text"
                placeholder="Buscar asesorías..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="AAP-search-input"
              />
              <Form.Select
                value={filterProfesor}
                onChange={(e) => setFilterProfesor(e.target.value)}
                className="AAP-filter-select"
              >
                <option value="">Todos los profesores</option>
                {profesores.map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.Nombre} {prof.apellido}
                  </option>
                ))}
              </Form.Select>
              <Form.Select
                value={filterDisponible}
                onChange={(e) => setFilterDisponible(e.target.value)}
                className="AAP-filter-select"
              >
                <option value="">Todas</option>
                <option value="true">Disponibles</option>
                <option value="false">No Disponibles</option>
              </Form.Select>
            </div>

            {/* Profesores Section */}
            {hasViewProfesores && (
              <section className="AAP-profesores-section">
                <div className="AAP-profesores-header" onClick={() => setExpandedProfesores(!expandedProfesores)}>
                  <h2 className="AAP-profesores-title">
                    <i className={`bi-chevron-${expandedProfesores ? 'down' : 'right'} AAP-profesores-chevron`} />
                    Profesores
                  </h2>
                  {expandedProfesores && hasCreateProfesores && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCreateProfesorModal(true);
                      }}
                    >
                      <i className="bi-plus-circle me-2" />
                      Crear Profesor
                    </Button>
                  )}
                </div>
                {expandedProfesores && (
                  <div className="AAP-profesores-content">
                    {/* Profesores Filters */}
                    <div className="AAP-profesores-filters">
                      <Form.Control
                        type="text"
                        placeholder="Buscar profesores..."
                        value={searchProfesores}
                        onChange={(e) => setSearchProfesores(e.target.value)}
                        className="AAP-profesores-search-input"
                      />
                      <Form.Select
                        value={filterEspecialidad}
                        onChange={(e) => setFilterEspecialidad(e.target.value)}
                        className="AAP-profesores-filter-select"
                      >
                        <option value="">Todas las especialidades</option>
                        {especialidades.map((esp) => (
                          <option key={esp} value={esp}>
                            {esp}
                          </option>
                        ))}
                      </Form.Select>
                    </div>

                    {/* Profesores List */}
                    {loadingProfesores ? (
                      <div className="AAP-profesores-skeletons">
                        {[...Array(6)].map((_, i) => (
                          <div key={i} className="AAP-profesor-skeleton">
                            <div className="AAP-profesor-skeleton-image" />
                            <div className="AAP-profesor-skeleton-body">
                              <div className="AAP-profesor-skeleton-line AAP-profesor-skeleton-line--short" />
                              <div className="AAP-profesor-skeleton-line AAP-profesor-skeleton-line--medium" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : profesores.length === 0 ? (
                      <div className="AAP-profesores-empty">
                        <i className="bi-person-badge" />
                        <p>No hay profesores creados.</p>
                        {hasCreateProfesores && (
                          <Button variant="outline-primary" size="sm" onClick={() => setShowCreateProfesorModal(true)}>
                            <i className="bi-plus-circle me-2" />
                            Crear primer profesor
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="AAP-profesores-grid">
                        {profesores.map((profesor) => (
                          <div key={profesor.id} className="AAP-profesor-card">
                            <div className="AAP-profesor-image-wrapper">
                              {profesor.Foto ? (
                                <img
                                  src={resolveImageUrl(profesor.Foto)}
                                  alt={`${profesor.Nombre} ${profesor.apellido}`}
                                  className="AAP-profesor-image"
                                />
                              ) : (
                                <div className="AAP-profesor-image-placeholder">
                                  <i className="bi-person-badge" />
                                </div>
                              )}
                            </div>
                            <div className="AAP-profesor-body">
                              <div className="AAP-profesor-header">
                                <h3 className="AAP-profesor-name">
                                  {profesor.Nombre} {profesor.apellido}
                                </h3>
                                {profesor.Especialidad && (
                                  <Badge bg="primary" className="AAP-profesor-specialty">
                                    {profesor.Especialidad}
                                  </Badge>
                                )}
                              </div>
                              {profesor.Descripcion && (
                                <p className="AAP-profesor-description">
                                  {profesor.Descripcion.substring(0, 150)}
                                  {profesor.Descripcion.length > 150 ? '...' : ''}
                                </p>
                              )}
                              <div className="AAP-profesor-actions">
                                {hasEditProfesores && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => navigate(`/admin/profesores/${profesor.id}`)}
                                  >
                                    <i className="bi-pencil me-1" />
                                    Editar
                                  </Button>
                                )}
                                {hasDeleteProfesores && (
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={() => handleDeleteProfesor(profesor.id, `${profesor.Nombre} ${profesor.apellido}`)}
                                  >
                                    <i className="bi-trash me-1" />
                                    Eliminar
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}

            {/* Asesorias List */}
            <div className="AAP-content">
              {loadingAsesorias ? (
                <div className="AAP-skeletons">
                  {[...Array(6)].map((_, i) => (
                    <AsesoriaSkeleton key={i} />
                  ))}
                </div>
              ) : asesorias.length === 0 ? (
                <EmptyState onCreate={hasCreateAsesorias ? () => setShowCreateModal(true) : null} />
              ) : (
                <div className="AAP-asesorias-grid">
                  {asesorias.map((asesoria) => (
                    <div
                      key={asesoria.id}
                      className="AAP-asesoria-card"
                      onClick={() => navigate(`/admin/asesorias/${asesoria.id}`)}
                    >
                      <div className="AAP-asesoria-header">
                        <div className="AAP-asesoria-title">
                          <h3>
                            {asesoria.profesor
                              ? `${asesoria.profesor.Nombre} ${asesoria.profesor.apellido}`
                              : 'Profesor no asignado'}
                          </h3>
                          {asesoria.profesor?.Especialidad && (
                            <span className="AAP-asesoria-specialty">
                              {asesoria.profesor.Especialidad}
                            </span>
                          )}
                        </div>
                        <Badge 
                          bg={asesoria.disponible ? 'success' : 'secondary'}
                          className={asesoria.disponible ? 'AAP-badge-disponible' : 'AAP-badge-no-disponible'}
                        >
                          {asesoria.disponible ? 'Disponible' : 'No Disponible'}
                        </Badge>
                      </div>
                      <div className="AAP-asesoria-body">
                        {asesoria.usuario && (
                          <div className="AAP-asesoria-info">
                            <i className="bi-person" />
                            <span>
                              {asesoria.usuario.nombre} {asesoria.usuario.apellido}
                            </span>
                          </div>
                        )}
                        {asesoria.start_time && (
                          <div className="AAP-asesoria-info">
                            <i className="bi-calendar-event" />
                            <span>{formatDate(asesoria.start_time)}</span>
                          </div>
                        )}
                        {asesoria.end_time && (
                          <div className="AAP-asesoria-info">
                            <i className="bi-clock" />
                            <span>Hasta: {formatDate(asesoria.end_time)}</span>
                          </div>
                        )}
                        <div className="AAP-asesoria-info">
                          <i className="bi-cash-coin" />
                          <span className="AAP-asesoria-price">{formatPrice(asesoria.Precio)}</span>
                        </div>
                        {asesoria.text && (
                          <div className="AAP-asesoria-text">
                            <p>{asesoria.text.substring(0, 100)}...</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Create Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Nueva Asesoría</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Profesor *</Form.Label>
              <Form.Select
                value={createAsesoriaData.profesor_id}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, profesor_id: e.target.value })
                }
                required
              >
                <option value="">Selecciona un profesor</option>
                {profesores.map((prof) => (
                  <option key={prof.id} value={prof.id}>
                    {prof.Nombre} {prof.apellido} {prof.Especialidad ? `- ${prof.Especialidad}` : ''}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link de Zoom</Form.Label>
              <Form.Control
                type="url"
                value={createAsesoriaData.Zoom}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, Zoom: e.target.value })
                }
                placeholder="https://zoom.us/j/..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Precio (€) *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={createAsesoriaData.Precio}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, Precio: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fecha y Hora de Inicio</Form.Label>
              <Form.Control
                type="datetime-local"
                value={createAsesoriaData.start_time}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, start_time: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Fecha y Hora de Fin</Form.Label>
              <Form.Control
                type="datetime-local"
                value={createAsesoriaData.end_time}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, end_time: e.target.value })
                }
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Texto de Referencia</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={createAsesoriaData.text}
                onChange={(e) =>
                  setCreateAsesoriaData({ ...createAsesoriaData, text: e.target.value })
                }
                placeholder="Información adicional sobre la asesoría..."
              />
            </Form.Group>

            <Form.Check
              type="switch"
              id="disponible-switch"
              label="Disponible"
              checked={createAsesoriaData.disponible}
              onChange={(e) =>
                setCreateAsesoriaData({ ...createAsesoriaData, disponible: e.target.checked })
              }
            />
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateAsesoria}
            disabled={creatingAsesoria || !createAsesoriaData.profesor_id || !createAsesoriaData.Precio}
          >
            {creatingAsesoria ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Crear Asesoría
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create Profesor Modal */}
      <Modal show={showCreateProfesorModal} onHide={() => setShowCreateProfesorModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Crear Nuevo Profesor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Foto del Profesor</Form.Label>
              {profesorImagePreview ? (
                <div className="mb-2">
                  <img
                    src={profesorImagePreview}
                    alt="Preview"
                    style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain', borderRadius: '0.5rem', marginBottom: '0.5rem' }}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => {
                      setProfesorImageFile(null);
                      setProfesorImagePreview(null);
                      setCreateProfesorData(prev => ({ ...prev, Foto: '' }));
                    }}
                  >
                    <i className="bi-trash me-1" />
                    Eliminar
                  </Button>
                </div>
              ) : createProfesorData.Foto ? (
                <div className="mb-2">
                  <img
                    src={resolveImageUrl(createProfesorData.Foto)}
                    alt="Foto actual"
                    style={{ maxHeight: '200px', maxWidth: '100%', objectFit: 'contain', borderRadius: '0.5rem', marginBottom: '0.5rem' }}
                  />
                </div>
              ) : null}
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleProfesorImageChange}
                disabled={uploadingProfesorImage}
              />
              {profesorImageFile && !createProfesorData.Foto && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleUploadProfesorImage}
                  disabled={uploadingProfesorImage}
                >
                  {uploadingProfesorImage ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Subiendo...
                    </>
                  ) : (
                    <>
                      <i className="bi-upload me-1" />
                      Subir Foto
                    </>
                  )}
                </Button>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.Nombre}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Nombre: e.target.value })
                }
                placeholder="Nombre del profesor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Apellido *</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.apellido}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, apellido: e.target.value })
                }
                placeholder="Apellido del profesor"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Especialidad</Form.Label>
              <Form.Control
                type="text"
                value={createProfesorData.Especialidad}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Especialidad: e.target.value })
                }
                placeholder="Ej: Odontología, Ortodoncia, etc."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                value={createProfesorData.Descripcion}
                onChange={(e) =>
                  setCreateProfesorData({ ...createProfesorData, Descripcion: e.target.value })
                }
                placeholder="Descripción del profesor..."
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateProfesorModal(false);
            setCreateProfesorData({
              Nombre: '',
              apellido: '',
              Especialidad: '',
              Descripcion: '',
              Foto: '',
            });
            setProfesorImageFile(null);
            setProfesorImagePreview(null);
          }}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateProfesor}
            disabled={creatingProfesor || !createProfesorData.Nombre || !createProfesorData.apellido}
          >
            {creatingProfesor ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Creando...
              </>
            ) : (
              <>
                <i className="bi-check-circle me-2" />
                Crear Profesor
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const AdminAsesoriasPage = () => (
  <AdminNotificationsProvider>
    <AdminAsesoriasPageInner />
  </AdminNotificationsProvider>
);

export default AdminAsesoriasPage;

